import { Send, X } from 'lucide-react'
import { useState } from 'react'
import { sendContactEmail } from '../../services/emailService'

interface ContactButtonProps {
  subject?: string
  className?: string
  variant?: 'link' | 'button'
  children?: React.ReactNode
}

export function ContactButton({ 
  subject = 'General Inquiry', 
  className = '',
  variant = 'link',
  children
}: ContactButtonProps) {
  const [showContactForm, setShowContactForm] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState('')

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    // Pre-fill the message with the subject
    setFormData(prev => ({ ...prev, message: subject ? `Regarding: ${subject}\n\n` : '' }))
    setShowContactForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitMessage('')
    
    try {
      const result = await sendContactEmail({
        name: formData.name,
        organization: formData.organization || undefined,
        email: formData.email,
        message: formData.message
      })
      
      if (result.success) {
        setSubmitMessage('Thank you for your message! We\'ll be in touch soon.')
        setFormData({ name: '', organization: '', email: '', message: '' })
        setTimeout(() => {
          setShowContactForm(false)
          setSubmitMessage('')
        }, 3000)
      } else {
        setSubmitMessage(result.error || 'Failed to send message. Please try again.')
      }
    } catch (error) {
      console.error('Contact form error:', error)
      setSubmitMessage('An error occurred. Please try again or email us directly.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const buttonElement = variant === 'button' ? (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors ${className}`}
    >
      <Send className="w-4 h-4" />
      {children || 'Contact Us'}
    </button>
  ) : (
    <a
      href="#"
      onClick={handleClick}
      className={`text-blue-600 hover:text-blue-800 underline ${className}`}
    >
      {children || 'Contact us'}
    </a>
  )

  return (
    <>
      {buttonElement}
      
      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-md">
            {/* Form Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Contact Us</h3>
              <button
                onClick={() => {
                  setShowContactForm(false)
                  setSubmitMessage('')
                }}
                className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            
            {/* Form Content */}
            <form onSubmit={handleSubmit} className="p-5">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1.5">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder="John Doe"
                  />
                </div>
                
                <div>
                  <label htmlFor="organization" className="block text-sm font-medium text-gray-300 mb-1.5">
                    Organization
                  </label>
                  <input
                    type="text"
                    id="organization"
                    value={formData.organization}
                    onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder="Tourism Board / Cycling Club"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1.5">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder="john@example.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1.5">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
                    placeholder="Tell us about your inquiry..."
                  />
                </div>
              </div>
              
              {/* Submit Message */}
              {submitMessage && (
                <div className={`mt-4 p-3 rounded-lg ${
                  submitMessage.includes('Thank you') 
                    ? 'bg-emerald-500/10 border border-emerald-500/30' 
                    : 'bg-red-500/10 border border-red-500/30'
                }`}>
                  <p className={`text-sm ${
                    submitMessage.includes('Thank you') ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {submitMessage}
                  </p>
                </div>
              )}
              
              {/* Form Actions */}
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowContactForm(false)
                    setSubmitMessage('')
                  }}
                  className="flex-1 px-4 py-2 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-white font-medium transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}